import api from "./api";

export interface TrustedDevice {
  id: number;
  device_id: string;
  device_name: string;
  device_type: "web" | "app";
  browser_info: string | null;
  os_info: string | null;
  last_used_at: string | null;
  last_ip: string | null;
  is_active: boolean;
  created_at: string;
  is_current: boolean;
}

export interface TrustedDeviceList {
  devices: TrustedDevice[];
  total: number;
  max_allowed: number;
  can_add_more: boolean;
}

/** [관리자] 직원별 데스크톱 메신저 설치 현황 한 줄 */
export interface DesktopAppStatusItem {
  user_id: string;
  name: string;
  email: string;
  department_name: string | null;
  role: string;
  /** 데스크톱 메신저로 로그인한 신뢰 기기가 살아 있음 (설치만 하고 미로그인은 false) */
  installed: boolean;
  app_version: string | null;
  os_info: string | null;
  last_used_at: string | null;
  last_ip: string | null;
  /** 지금 데스크톱 메신저가 켜져 있음 */
  online: boolean;
}

export interface DesktopAppStatus {
  items: DesktopAppStatusItem[];
  total: number;
  installed_count: number;
  not_installed_count: number;
  online_count: number;
}

/** [관리자] 데스크톱 메신저 강제 설치 정책 */
export type DesktopAppForceMode = "off" | "all" | "selected";

export interface DesktopAppForcePolicy {
  mode: DesktopAppForceMode;
  /** selected 모드의 대상 사용자 ID. all/off 에서도 보존된다 */
  user_ids: string[];
  updated_at: string | null;
  updated_by_name: string | null;
  /** 목록에 남아 있지만 퇴사·비활성 등으로 실제 적용되지 않는 ID */
  ignored_user_ids: string[];
}

export interface DesktopAppForcePolicyUpdate {
  mode: DesktopAppForceMode;
  user_ids: string[];
}

export const trustedDeviceService = {
  /** 내 신뢰 기기 목록 조회 */
  async list(currentDeviceId?: string): Promise<TrustedDeviceList> {
    const response = await api.get<TrustedDeviceList>("/auth/trusted-devices", {
      headers: currentDeviceId ? { "X-Device-Id": currentDeviceId } : undefined,
    });
    return response.data;
  },

  /** 기기 이름 변경 */
  async updateName(deviceId: string, deviceName: string): Promise<TrustedDevice> {
    const response = await api.put<TrustedDevice>(
      `/auth/trusted-devices/${deviceId}`,
      { device_name: deviceName }
    );
    return response.data;
  },

  /**
   * 기기 삭제
   * @param block true면 삭제 후 24시간 동안 해당 기기 로그인 차단
   */
  async remove(deviceId: string, block: boolean = false): Promise<void> {
    await api.delete(`/auth/trusted-devices/${deviceId}`, {
      params: { block },
    });
  },

  /** [관리자] 특정 사용자의 신뢰 기기 목록 */
  async adminList(userId: string): Promise<TrustedDeviceList> {
    const response = await api.get<TrustedDeviceList>(
      `/auth/admin/users/${userId}/trusted-devices`
    );
    return response.data;
  },

  /** [관리자] 우리 회사 직원별 데스크톱 메신저 설치 현황 */
  async adminDesktopAppStatus(): Promise<DesktopAppStatus> {
    const response = await api.get<DesktopAppStatus>("/auth/admin/desktop-app-status");
    return response.data;
  },

  /** [관리자] 접속 중인 직원의 데스크톱 메신저에 "지금 업데이트" 지시 (앱이 30초 후 재시작·설치) */
  async adminForceDesktopUpdate(userId: string): Promise<{ sent: boolean; user_id: string }> {
    const response = await api.post<{ sent: boolean; user_id: string }>(
      `/auth/admin/desktop-app-status/${userId}/force-update`
    );
    return response.data;
  },

  /** [관리자] 데스크톱 메신저 강제 설치 정책 조회 */
  async adminGetDesktopForcePolicy(): Promise<DesktopAppForcePolicy> {
    const response = await api.get<DesktopAppForcePolicy>("/auth/admin/desktop-app-policy");
    return response.data;
  },

  /** [관리자] 데스크톱 메신저 강제 설치 정책 저장 (전체/선택/해제 + 대상 목록) */
  async adminPutDesktopForcePolicy(payload: DesktopAppForcePolicyUpdate): Promise<DesktopAppForcePolicy> {
    const response = await api.put<DesktopAppForcePolicy>("/auth/admin/desktop-app-policy", payload);
    return response.data;
  },

  /** [관리자] 특정 사용자의 기기 삭제 */
  async adminRemove(userId: string, deviceId: string): Promise<void> {
    await api.delete(`/auth/admin/users/${userId}/trusted-devices/${deviceId}`);
  },
};

export default trustedDeviceService;
